import React, { useCallback, useMemo, useContext } from 'react';
import { stringifySync } from '@bingads-webui/safe-json';

import PropTypes from 'prop-types';
import Promise from 'bluebird';
import { each, first, get, map, isObject, filter } from 'underscore';
import classnames from 'classnames';
import { makeStyles, Button } from '@fluentui/react-components';
import { ShareMultipleRegular } from '@fluentui/react-icons';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { Stack, IconButton } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  ImageFilePicker,
  generatePreviewArray,
  updateProgress,
  updateFilePreviewArrayUploadProgress,
} from '@bingads-webui-campaign-react/upload-file-picker';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  getImageValidationErrorMessage,
  validateImageSyncAsyncWithDimensionParams,
  getImageDetailAsync,
} from '@bingads-webui-campaign/image-experience-utils';
import { handleRealUsedFlag, RealUsedFlagSuffix } from '@bingads-webui-campaign-react/asset-library-model';

import { getMediaSourcesContainerStyles } from './styles';

const MAX_UPSCALED_SIZE = 4;

const useStyles = makeStyles({
  investBtnWrapper: {
    '&:hover, &:active, &:focus, &:focus-within': {
      color: '#0078d4',
      backgroundColor: '#f0f0f0',
      borderTopLeftRadius: '99px',
      borderTopRightRadius: '99px',
      borderBottomLeftRadius: '99px',
      borderBottomRightRadius: '99px',
    },
    '& span.fui-Button__icon': {
      width: '30px',
      height: '30px',
      fontSize: '30px',
    },
  },
  investBtn: {
    width: '52px',
    minWidth: '52px',
    height: '52px',
    color: '#0078d4',
    backgroundColor: '#DEECF9',
    borderTopLeftRadius: '99px',
    borderTopRightRadius: '99px',
    borderBottomLeftRadius: '99px',
    borderBottomRightRadius: '99px',
    cursor: 'pointer',
  },
});

export const AddMediaSourcesContainer = ({
  setUploadPreviewMediaArr,
  setShowUploadPreview,
  setUploadErrors,
  setHideSubtitle,
  buttonConfig,
  goToView,
  uploadMediaConfig: {
    maxUploadNum,
    minSize,
    minUploadSize,
    uploadMedia,
    processUploadedMedia,
    onProcessUploadComplete,
  },
  logger,
  assetLibraryConfig,
  supportImageUpscale,
}) => {
  const styles = useStyles();

  const classes = useDefaultStyles(getMediaSourcesContainerStyles);
  const i18n = useContext(I18nContext);

  const legacyI18n = useMemo(() => window.globalLegacyI18n.get('$/i18n!legacy/global'), []);

  // TODO: check whether the video upload can reuse this function, if not pass as props
  const onFileRead = useCallback((imageSources, uploadMediaInfo) => {
    // if more than the max number of uploadable images is selected
    // just upload the first images in the array up to that max number
    const numToUpload = Math.min(maxUploadNum, imageSources.length);
    const imagesToUpload = first(imageSources, numToUpload);
    const isMultipleImages = imagesToUpload.length > 1;

    setUploadErrors([]);

    const isImageUpscaled = [];
    const minimumHeight = minUploadSize.length > 1 ? minUploadSize[1] : minSize[1];
    const minimumWidth = minUploadSize.length > 1 ? minUploadSize[0] : minSize[0];

    const validateImagesPromise = Promise.all(imagesToUpload.map((imageSource, index) => validateImageSyncAsyncWithDimensionParams({
      imageSource,
      minimumHeight,
      minimumWidth,
    })
      .then(() => getImageDetailAsync(imageSource))
      .then((img) => {
        if (supportImageUpscale && minUploadSize.length > 1 && minSize.length > 1 && img &&
          ((img.naturalWidth >= minUploadSize[0] && img.naturalWidth < minSize[0]) ||
            (img.naturalHeight >= minUploadSize[1] && img.naturalHeight < minSize[1]))
        ) {
          isImageUpscaled[index] = true;
        } else {
          isImageUpscaled[index] = false;
        }
      }).catch((e) => {
        if (e && e.message === 'SizeValidationError' && logger && logger.logTrace) {
          logger.logTrace(`Ad creation flow per file - SizeValidationError: ${stringifySync(e)}`);
          logger.logTrace(`Ad creation flow per file - SizeValidationError - ImageSource: ${stringifySync(imageSource)}`);
        }
        throw e;
      })));

    const doubleValidationImagesPromise = validateImagesPromise
      .then(() => {
        const needUpscaledImage = filter(isImageUpscaled);
        if (needUpscaledImage.length > MAX_UPSCALED_SIZE) {
          // When the need upscaled image count over the max size, should skip the upscale feature and use original threshold to validate again.
          return Promise.all(imagesToUpload.map((imageSource, index) => {
            if (isImageUpscaled[index] === true) {
              return validateImageSyncAsyncWithDimensionParams({
                imageSource,
                minimumHeight: minSize[1],
                minimumWidth: minSize[0],
              }).catch((e) => {
                if (e && e.message === 'SizeValidationError' && logger && logger.logTrace) {
                  logger.logTrace(`Ad creation flow per file - SizeValidationError: ${stringifySync(e)}`);
                  logger.logTrace(`Ad creation flow per file - SizeValidationError - ImageSource: ${stringifySync(imageSource)}`);
                }
                throw e;
              });
            }
            return Promise.resolve();
          }));
        }
        return Promise.resolve();
      });

    return doubleValidationImagesPromise
      .then(() => {
        const imagesForPreview = generatePreviewArray({
          files: uploadMediaInfo,
          fileSources: imagesToUpload,
        });

        setUploadPreviewMediaArr(imagesForPreview);
        setShowUploadPreview(true);
        setHideSubtitle(true);

        const setProgress = (index, progress) => {
          setUploadPreviewMediaArr((prevArr) => {
            const newState =
              updateFilePreviewArrayUploadProgress(index, progress)({ previewArr: prevArr });

            return newState.previewArr;
          });
        };

        const imageUploadPromises = [];

        each(imagesToUpload, (imageSource, index) => {
          imageUploadPromises.push(uploadMedia({
            imageData: imageSource,
            updateCurrentProgress: updateProgress({
              setProgress,
              index,
              uploadProgressBoundary: 1,
            }),
            name: uploadMediaInfo[index].name,
            index,
          }));
        });

        return Promise.all(imageUploadPromises)
          .then((images) => {
            if (images) {
              const imageIds = images && images.map(v => v.imageId).join(',');
              logger.logTrace('Ad creation flow - uploaded local images', { imageIds, count: images.length });
            }
            const processPromises = [];

            each(images, ({ imageId }, index) => {
              processPromises.push(processUploadedMedia({
                imageId,
                index,
                updateCurrentProgress: updateProgress({
                  setProgress,
                  index,
                  uploadProgressBoundary: 1,
                }),
              }));
            });

            return Promise.all(processPromises)
              .then((args) => {
                const newArgs = map(args, (item, index) => {
                  if (isObject(item)) {
                    return {
                      ...item,
                      isImageUpscaled: isImageUpscaled[index],
                    };
                  }
                  return item;
                });
                return onProcessUploadComplete(newArgs);
              });
          });
      })
      .catch((e) => {
        let error = e;

        if (!e || e.message !== 'SizeValidationError') {
          const code = e && e.body && e.body.value;
          error = { message: get(code, [0, 'Code']) };
        }

        setShowUploadPreview(false);
        setUploadPreviewMediaArr([]);
        const skipDimension = supportImageUpscale && minUploadSize.length > 1;
        const validationError = getImageValidationErrorMessage(error, legacyI18n, isMultipleImages, skipDimension);
        setUploadErrors([
          validationError || (
            <span>
              {i18n.getString(_TL_('One or more assets failed to upload. Please try again'))}
              <LearnMoreLink linkText={i18n.getString(_TL_('Learn more about asset guidelines and accepted file types.'))} topic="app54823" />
            </span>
          ),
        ]);
        logger.logTrace(`Ad creation flow - uploaded local images failed, error: ${validationError || 'Failed to upload'}`);
        if (error && error.message === 'SizeValidationError' && logger && logger.logTrace) {
          logger.logTrace(`Ad creation flow - SizeValidationError: ${stringifySync(error)}`);
        }
      });
  }, [
    setUploadPreviewMediaArr,
    setShowUploadPreview,
    uploadMedia,
    setUploadErrors,
    maxUploadNum,
    setHideSubtitle,
    legacyI18n,
    minSize,
    processUploadedMedia,
    logger,
    i18n,
    minUploadSize,
    onProcessUploadComplete,
    supportImageUpscale,
  ]);

  const onMediaButtonClick = useCallback((view) => {
    const { assetLibraryAccountPreferences } = assetLibraryConfig;
    handleRealUsedFlag(assetLibraryAccountPreferences, RealUsedFlagSuffix.Picker);
    return goToView(view);
  }, [assetLibraryConfig, goToView]);

  return (
    <Stack className={classnames(classes.container, 'media-sources-and-upload-container')} horizontal>
      <Stack.Item grow={0} className="media-sources-container" align="center">
        <Stack horizontal horizontalAlign="center" className={classes.mediaSources}>
          {
            buttonConfig.map(({
              iconName,
              iconStyles,
              label,
              view,
              buttonLocator,
            }) => (
              <Stack.Item align="center" grow className={classnames(classes.mediaSource, buttonLocator)}>
                {buttonLocator === 'invest-images' &&
                  <div className={styles.investBtnWrapper}>
                    <Button
                      className={styles.investBtn}
                      icon={<ShareMultipleRegular />}
                      onClick={() => onMediaButtonClick(view)}
                      ariaLabel={i18n.getString(label)}
                    />
                  </div>
                }
                {buttonLocator !== 'invest-images' &&
                  <IconButton
                    iconProps={{
                      iconName,
                      styles: {
                        root: {
                          fontSize: 24,
                        },
                        ...iconStyles,
                      },
                    }}
                    onClick={() => onMediaButtonClick(view)}
                    styles={{
                      root: {
                        width: 52,
                        height: 52,
                        background: '#DEECF9',
                        borderRadius: '99px',
                      },
                    }}
                    ariaLabel={i18n.getString(label)}
                  />
                }
                <div className={classes.mediaSourceLabel}>
                  {i18n.getString(label)}
                </div>
              </Stack.Item>
            ))
          }
        </Stack>
      </Stack.Item>
      <Stack.Item grow={1} className="upload-container">
        <ImageFilePicker
          onFileRead={onFileRead}
          showErrors={setUploadErrors}
          i18n={legacyI18n}
          enableMultiple
          enableDrag
          hideDescription
          minimumSize={minSize}
          maxUploadNum={maxUploadNum}
          useButton
        />
      </Stack.Item>
    </Stack>
  );
};

AddMediaSourcesContainer.propTypes = {
  setUploadPreviewMediaArr: PropTypes.func.isRequired,
  setShowUploadPreview: PropTypes.func.isRequired,
  uploadMediaConfig: PropTypes.shape({
    uploadMedia: PropTypes.func.isRequired,
    maxUploadNum: PropTypes.number.isRequired,
    minSize: PropTypes.arrayOf(PropTypes.number).isRequired,
    minUploadSize: PropTypes.arrayOf(PropTypes.number),
    processUploadedMedia: PropTypes.func.isRequired,
    onProcessUploadComplete: PropTypes.func.isRequired,
  }).isRequired,
  setUploadErrors: PropTypes.func.isRequired,
  setHideSubtitle: PropTypes.func.isRequired,
  buttonConfig: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
  })).isRequired,
  goToView: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  assetLibraryConfig: PropTypes.shape({
    assetLibraryAccountPreferences: PropTypes.shape({}),
  }).isRequired,
  supportImageUpscale: PropTypes.bool,
};

AddMediaSourcesContainer.defaultProps = {
  supportImageUpscale: false,
};
